import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Eye, Pencil, Plus, Check, X, Trash2, MoreVertical,
  MessageSquare, Upload,
} from "lucide-react";
import FeedbackFormDialog from "./FeedbackFormDialog";
import FeedbackViewDialog, { STATUS_BADGES } from "./FeedbackViewDialog";

/* eslint-disable react-hooks/exhaustive-deps */

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export default function ProgressFeedbackPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();

  // Filters
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formStudent, setFormStudent] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [actionMenuFor, setActionMenuFor] = useState(""); // feedback id

  // Reject dialog
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Import dialog
  const fileInputRef = React.useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const branchId = selectedBranch?.id;

  /* Lookups */
  useEffect(() => {
    if (!branchId) { setClasses([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  useEffect(() => {
    setSectionId("");
    if (!classId || !branchId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, classId]);

  // Students list — branch-wide so the picker has options
  useEffect(() => {
    if (!branchId) { setStudents([]); return; }
    (async () => {
      try {
        const params = { branch_id: branchId };
        if (sectionId) params.section_id = sectionId;
        const { data } = await api.get("/students", { params });
        setStudents((data || []).filter((s) => s.status !== "inactive"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, sectionId]);

  /* Load feedback list */
  const loadItems = useCallback(async () => {
    if (!branchId) { setItems([]); return; }
    setLoading(true);
    try {
      const params = { branch_id: branchId };
      if (classId) params.class_id = classId;
      if (sectionId) params.section_id = sectionId;
      if (studentId) params.student_id = studentId;
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/progress-feedback", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, classId, sectionId, studentId, statusFilter, dateFrom, dateTo, search]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // Close action menu when clicking elsewhere
  useEffect(() => {
    const handler = () => setActionMenuFor("");
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  /* Actions */
  const handleCreate = (student) => {
    setFormStudent(student); setEditing(null); setFormOpen(true);
  };
  const handleEdit = async (fb) => {
    setEditing(fb);
    // Find the underlying student so we have the latest enrollment context
    const stu = students.find((s) => s.id === fb.student_id) || { id: fb.student_id, full_name: fb.student_name };
    setFormStudent(stu);
    setFormOpen(true);
  };
  const handleView = (fb) => setViewing(fb);
  const handleApprove = async (fb) => {
    try {
      await api.post(`/progress-feedback/${fb.id}/approve`);
      toast.success("Approved");
      loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openReject = (fb) => { setRejectFor(fb); setRejectReason(""); };
  const submitReject = async () => {
    try {
      await api.post(`/progress-feedback/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejectFor(null);
      loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDelete = async (fb) => {
    if (!window.confirm(`Delete this feedback for ${fb.student_name}?`)) return;
    try {
      await api.delete(`/progress-feedback/${fb.id}`);
      toast.success("Deleted");
      loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleAddForSame = (fb) => {
    const stu = students.find((s) => s.id === fb.student_id) || { id: fb.student_id, full_name: fb.student_name };
    handleCreate(stu);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/progress-feedback/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} feedback entries`);
      else toast.info("Nothing imported");
      loadItems();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <div className="mb-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Progress Feedback</h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm">Write parent-friendly feedback about a student's progress, behavior and participation.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFile}
            className="hidden"
            data-testid="pf-import-file"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={!branchId || importing}
            className="rounded-2xl h-11 px-5"
            data-testid="pf-import-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" /> {importing ? "Importing..." : "Import CSV"}
          </Button>
          <Button onClick={() => setStudentPickerOpen(true)} disabled={!branchId}
            className="rounded-2xl h-11 px-5 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="pf-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Feedback
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="pf-filters">
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <Field label="BRANCH">
            <select value={branchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)} className="pf-select" data-testid="pf-branch">
              {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="CLASS / LEVEL">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="pf-select" data-testid="pf-class">
              <option value="">All levels</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="SECTION">
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId} className="pf-select" data-testid="pf-section">
              <option value="">All sections</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          <Field label="STUDENT">
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} className="pf-select" data-testid="pf-student-filter">
              <option value="">All students</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.name} ${s.last_name}`.trim()}</option>)}
            </select>
          </Field>
          <Field label="STATUS">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="pf-select" data-testid="pf-status-filter">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="FROM">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl h-11" data-testid="pf-from" />
          </Field>
          <Field label="TO">
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl h-11" data-testid="pf-to" />
          </Field>
        </div>
        <Field label="SEARCH">
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by student, title, sub-title, feedback, created by..."
            className="rounded-xl h-11" data-testid="pf-search" />
        </Field>
      </section>

      {/* Table */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="pf-table-wrap">
        {loading ? <p className="p-6 text-brand-inkMuted">Loading...</p>
          : items.length === 0 ? (
            <EmptyState onCreate={() => setStudentPickerOpen(true)} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-brand-purpleLight/40 text-foreground">
                    <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Student Name</th>
                    <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Date</th>
                    <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Title</th>
                    <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((fb) => {
                    const badge = STATUS_BADGES[fb.status] || STATUS_BADGES.draft;
                    return (
                      <tr key={fb.id} className="border-t border-brand-purple/10 hover:bg-brand-purpleLight/20" data-testid={`pf-row-${fb.id}`}>
                        <td className="px-4 py-3 font-heading font-semibold uppercase">{fb.student_name}</td>
                        <td className="px-4 py-3 text-brand-inkMuted">{fb.date}</td>
                        <td className="px-4 py-3 uppercase tracking-wider text-xs font-heading text-foreground">{fb.title}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center text-[10px] font-heading font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${badge.cls}`} data-testid={`pf-status-${fb.id}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setActionMenuFor(actionMenuFor === fb.id ? "" : fb.id); }}
                            className="p-2 rounded-lg hover:bg-brand-purpleLight/40"
                            data-testid={`pf-actions-${fb.id}`}
                          >
                            <MoreVertical className="w-4 h-4 text-brand-inkMuted" />
                          </button>
                          {actionMenuFor === fb.id && (
                            <ActionMenu
                              fb={fb}
                              onView={() => { handleView(fb); setActionMenuFor(""); }}
                              onEdit={() => { handleEdit(fb); setActionMenuFor(""); }}
                              onAdd={() => { handleAddForSame(fb); setActionMenuFor(""); }}
                              onApprove={() => { handleApprove(fb); setActionMenuFor(""); }}
                              onReject={() => { openReject(fb); setActionMenuFor(""); }}
                              onDelete={() => { handleDelete(fb); setActionMenuFor(""); }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </section>

      {/* Student picker modal — choose who the feedback is for */}
      {studentPickerOpen && (
        <StudentPicker
          students={students}
          onCancel={() => setStudentPickerOpen(false)}
          onPick={(stu) => {
            setStudentPickerOpen(false);
            handleCreate(stu);
          }}
        />
      )}

      <FeedbackFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={loadItems}
        branchId={branchId}
        student={formStudent}
        existing={editing}
      />
      <FeedbackViewDialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        feedback={viewing}
      />

      {/* Reject dialog */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejectFor(null)} data-testid="pf-reject-dialog">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-brand-purple/10">
              <h2 className="font-heading text-xl font-bold text-foreground">Reject Feedback</h2>
              <p className="text-sm text-brand-inkMuted mt-1">For: <span className="font-heading text-foreground uppercase">{rejectFor.student_name}</span></p>
            </div>
            <div className="p-5">
              <p className="text-sm text-brand-inkMuted mb-2">Reason (optional)</p>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                rows={4} className="w-full rounded-xl border border-brand-purple/20 p-3 text-sm"
                placeholder="Tell the teacher what needs to change..." data-testid="pf-reject-reason" />
            </div>
            <div className="p-5 border-t border-brand-purple/10 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectFor(null)} className="rounded-2xl">Cancel</Button>
              <Button onClick={submitReject} className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="pf-reject-confirm">
                <X className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import result dialog */}
      {importResult && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setImportResult(null)} data-testid="pf-import-result">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-brand-purple/10">
              <h2 className="font-heading text-xl font-bold text-foreground">Import Result</h2>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <Stat label="Imported" value={importResult.imported} tone="green" />
              <Stat label="Skipped (empty title/feedback)" value={importResult.skipped_empty} />
              <Stat label="Skipped (no matching branch)" value={importResult.skipped_no_branch} tone={importResult.skipped_no_branch ? "red" : ""} />
              <Stat label="Skipped (no matching student)" value={importResult.skipped_no_student} tone={importResult.skipped_no_student ? "red" : ""} />
              <Stat label="Skipped (duplicate)" value={importResult.skipped_duplicate} />
              {importResult.errors?.length > 0 && (
                <div className="mt-3 border-t border-brand-purple/10 pt-3">
                  <p className="font-heading font-semibold text-foreground mb-1">First {importResult.errors.length} errors</p>
                  <ul className="text-xs text-[#C62828] space-y-0.5 max-h-40 overflow-y-auto">
                    {importResult.errors.map((er, i) => <li key={i}>• {er}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-brand-purple/10 flex justify-end">
              <Button onClick={() => setImportResult(null)} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="pf-import-result-close">Close</Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pf-select {
          width: 100%; height: 2.75rem; border-radius: 0.75rem;
          border: 1px solid rgba(37, 99, 235, 0.2); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="p-12 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <MessageSquare className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">No progress feedback yet</p>
      <p className="text-brand-inkMuted text-sm mt-1">Click "New Feedback" to write parent-friendly notes about a student.</p>
      <Button onClick={onCreate} className="mt-4 rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="pf-empty-create">
        <Plus className="w-4 h-4 mr-1.5" /> Create First Feedback
      </Button>
    </div>
  );
}

function ActionMenu({ fb, onView, onEdit, onAdd, onApprove, onReject, onDelete }) {
  const canApprove = fb.status === "pending_approval" || fb.status === "rejected";
  const canReject = fb.status === "pending_approval" || fb.status === "approved";
  return (
    <div
      className="absolute right-2 top-full mt-1 bg-white rounded-2xl shadow-xl border border-brand-purple/15 py-1 min-w-[160px] z-20"
      onClick={(e) => e.stopPropagation()}
      data-testid={`pf-action-menu-${fb.id}`}
    >
      <MenuItem icon={<Eye className="w-4 h-4" />} label="View" onClick={onView} testId={`pf-view-${fb.id}`} />
      <MenuItem icon={<Pencil className="w-4 h-4" />} label="Edit" onClick={onEdit} testId={`pf-edit-${fb.id}`} />
      <MenuItem icon={<Plus className="w-4 h-4" />} label="Add" onClick={onAdd} testId={`pf-add-${fb.id}`} />
      {canApprove && <MenuItem icon={<Check className="w-4 h-4 text-[#2E7D32]" />} label="Approve" onClick={onApprove} testId={`pf-approve-${fb.id}`} />}
      {canReject && <MenuItem icon={<X className="w-4 h-4 text-[#C62828]" />} label="Reject" onClick={onReject} testId={`pf-reject-${fb.id}`} />}
      <div className="border-t border-brand-purple/10 my-1" />
      <MenuItem icon={<Trash2 className="w-4 h-4 text-[#C62828]" />} label="Delete" onClick={onDelete} testId={`pf-delete-${fb.id}`} danger />
    </div>
  );
}

function MenuItem({ icon, label, onClick, testId, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-sm font-heading hover:bg-brand-purpleLight/30 flex items-center gap-2 ${danger ? "text-[#C62828]" : "text-foreground"}`}
    >
      {icon}{label}
    </button>
  );
}

function Stat({ label, value, tone }) {
  const tones = { green: "text-[#2E7D32]", red: "text-[#C62828]" };
  return (
    <div className="flex items-center justify-between" data-testid={`pf-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className="text-brand-inkMuted">{label}</span>
      <span className={`font-heading font-bold tabular-nums ${tones[tone] || "text-foreground"}`}>{value}</span>
    </div>
  );
}


function StudentPicker({ students, onCancel, onPick }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return students.filter((stu) =>
      (stu.full_name || `${stu.name || ""} ${stu.last_name || ""}`).toLowerCase().includes(s)
    );
  }, [q, students]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="pf-student-picker">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-brand-purple/10">
          <h2 className="font-heading text-xl font-bold text-foreground">Pick a Student</h2>
          <p className="text-sm text-brand-inkMuted">Who is this feedback for?</p>
        </div>
        <div className="p-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="rounded-xl h-11" data-testid="pf-picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {filtered.length === 0
            ? <p className="text-center text-sm text-brand-inkMuted py-6">No students match.</p>
            : filtered.map((stu) => (
              <button key={stu.id} onClick={() => onPick(stu)}
                className="w-full text-left p-3 rounded-xl hover:bg-brand-purpleLight/40 font-heading text-foreground"
                data-testid={`pf-picker-stu-${stu.id}`}
              >
                {stu.full_name || `${stu.name} ${stu.last_name}`.trim()}
              </button>
            ))
          }
        </div>
        <div className="p-3 border-t border-brand-purple/10 flex justify-end">
          <Button variant="outline" onClick={onCancel} className="rounded-2xl">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
